import React, { useState } from 'react';
import {
  Box,
  Button,
  Column,
  Copy,
  Flex,
  Row,
  Square,
  Stack,
  SvgInfo,
  SvgPencil,
  SvgX,
} from '@grubhubprod/gh-cookbook-react';
import { getFormattedPhoneNumber } from 'src/components/PhoneMaskInput/PhoneMaskInput';
import { SimpleDialog } from 'src/components/SimpleDialog/SimpleDialog';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { pushErrorFromAPI, pushSuccess } from 'src/utilities/notifications';
import { EnterpriseInvoiceRecipientModal } from 'src/components/enterprise/set-up/steps/EnterpriseInvoiceSettingsStep/EnterpriseInvoiceRecipientModal';
import {
  deleteInvoiceContact,
  IInvoiceContact,
  SubtotalInvoiceMethod,
} from 'src/utilities/invoiceContact.utils';

export interface IEnterpriseInvoiceRecipientTableRowProps {
  corpId: string;
  invoiceContact: IInvoiceContact;
  isLastRow: boolean;
  onRemovalSuccess: () => void;
  onEditSuccess: () => void;
}

export function EnterpriseInvoiceRecipientTableRow({
  corpId,
  invoiceContact,
  isLastRow,
  onRemovalSuccess,
  onEditSuccess,
}: IEnterpriseInvoiceRecipientTableRowProps): JSX.Element {
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isRemovalInProgress, setIsRemovalInProgress] = useState(false);
  const { invoice_contact_id, first_name, last_name, email, primary, phone } =
    invoiceContact;
  const fullContactName = `${first_name} ${last_name}`;

  async function removeInvoiceContact(invoiceContactId: string): Promise<void> {
    try {
      setIsRemovalInProgress(true);

      await deleteInvoiceContact({
        corpId,
        subtotalMethod: SubtotalInvoiceMethod.MEAL_TYPE,
        invoiceContactId,
      });

      setShowRemoveConfirmation(false);
      onRemovalSuccess();
      pushSuccess(`${fullContactName} was removed`);
    } catch (error) {
      setIsRemovalInProgress(false);
      pushErrorFromAPI(error, {
        fallbackMessage: `${fullContactName} was not removed. Try again.`,
      });
    }
  }
  return (
    <>
      <Square
        key={invoice_contact_id}
        as="li"
        inset="sm"
        borderBottom={isLastRow ? 'none' : '1px solid'}
        borderColor="lightLine"
      >
        <Row gutter={8}>
          <Column phone={4} gutter={8}>
            <div className="ellipsisOverflow">
              <Copy variant="body" color="primaryText">
                {fullContactName}
              </Copy>
            </div>

            {primary && (
              <Flex alignItems="center" data-testid="primary-contact-subline">
                <Stack x="x-sm">
                  <Copy variant="body" color="secondaryText">
                    (Primary contact)
                  </Copy>
                </Stack>
                <Tooltip
                  roundedContainer
                  maxWidth="280px"
                  content={
                    <Copy variant="body" color="invertedPrimaryText">
                      To change or remove a primary contact, reach out to your
                      Client Success Manager.
                    </Copy>
                  }
                >
                  <div>
                    <Box color="secondaryText">
                      <SvgInfo />
                    </Box>
                  </div>
                </Tooltip>
              </Flex>
            )}
          </Column>
          <Column phone={3} gutter={8} className="ellipsisOverflow">
            <Copy variant="body" color="primaryText">
              {email}
            </Copy>
          </Column>
          <Column phone={3} gutter={8} className="ellipsisOverflow">
            <Copy variant="body" color="primaryText">
              {getFormattedPhoneNumber(phone)}
            </Copy>
          </Column>
          <Column phone={2} gutter={8}>
            <Flex
              width="100%"
              flexDirection="row"
              justifyContent="flex-end"
              alignItems="center"
            >
              <Stack x="md">
                <Tooltip
                  roundedContainer
                  content={
                    <Copy variant="body" color="invertedPrimaryText">
                      Edit
                    </Copy>
                  }
                >
                  <div>
                    <Button
                      variant="tertiary"
                      disabled={primary}
                      className="tableActionButtons"
                      onClick={() => {
                        if (!primary) {
                          setShowEditModal(true);
                        }
                      }}
                    >
                      <SvgPencil aria-label="Edit" />
                    </Button>
                  </div>
                </Tooltip>
              </Stack>
              <Tooltip
                roundedContainer
                content={
                  <Copy variant="body" color="invertedPrimaryText">
                    Delete
                  </Copy>
                }
              >
                <div>
                  <Button
                    variant="tertiary"
                    disabled={primary}
                    className="tableActionButtons"
                    onClick={() => {
                      if (!primary) {
                        setShowRemoveConfirmation(true);
                      }
                    }}
                  >
                    <SvgX aria-label="Remove" />
                  </Button>
                </div>
              </Tooltip>
            </Flex>
          </Column>
        </Row>
      </Square>
      {showRemoveConfirmation && (
        <SimpleDialog
          id={invoice_contact_id}
          primaryActionFlavor="danger"
          title={`Remove "${fullContactName}"?`}
          description="You’re about to remove this invoice recipient."
          primaryActionText="Yes, remove"
          isPrimaryButtonLoading={isRemovalInProgress}
          buttonsDirection="row"
          secondaryActionText="Cancel"
          disablePrimaryActionButton={isRemovalInProgress}
          disableSecondaryActionButton={isRemovalInProgress}
          onSecondaryActionClick={() => setShowRemoveConfirmation(false)}
          onPrimaryActionClick={(id) => removeInvoiceContact(id)}
        />
      )}

      {showEditModal && (
        <EnterpriseInvoiceRecipientModal
          invoiceContact={invoiceContact}
          corpId={corpId}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => onEditSuccess()}
        />
      )}
    </>
  );
}
